import { AdmissionRules } from '@quromedical/business-rules';
import { DiagnosisTypeCode, GroupSecurity } from '@quromedical/fhir-common';
import {
  Admission,
  PatientResponse,
  WithId,
  AdmissionUpdateRequest,
  PatientGeneralPractitionerUpdateRequest,
  PatientCoverageResponse,
} from '@quromedical/models';
import { compact } from '@quromedical/utils';
import { Row } from 'components/base';
import { FileResult, SelectOption } from 'components/types';
import {
  Button,
  FetcherSearchOptions,
  MARGIN,
  Section,
  Skeleton,
  SkeletonProvider,
  Spacer,
} from 'design-system';
import { useUserSession } from 'hooks';
import { CreateUploadUrl } from 'hooks/useFileUpload';
import { useFiniteState } from 'hooks/useFiniteState';
import { useObjectState } from 'hooks/useObjectState';
import { locationWardFetcher } from 'integration';
import { CoverageApi, PatientApi, PatientEndpoint } from 'integration/aggregate';
import { usePatientId } from 'providers/PatientIdContext';
import React, { useCallback, useMemo } from 'react';
import { strings } from 'strings';
import useSWR from 'swr';

import {
  AdmissionAuthorizationData,
  AdmissionAuthorizationForm,
} from './AdmissionAuthorizationForm';
import {
  AdmissionClinicalData,
  AdmissionClinicalForm,
  mapDiagnosesToDiagnosisTypeCode,
} from './AdmissionClinicalForm';
import { AdmissionLocationForm } from './AdmissionLocationForm';
import { AdmissionNotesData, AdmissionNotesForm } from './AdmissionNotesForm';
import { AdmissionStatusDisplay } from './AdmissionStatusDisplay';
import { getOxygenValue, OxygenFields } from './internal';

interface AdmissionUpdateFormProps {
  data: WithId<Partial<Admission>>;
  icd10CodeFetcher: (query?: FetcherSearchOptions) => Promise<SelectOption[]>;
  handleSubmit: (data: AdmissionUpdateRequest) => void;
  handleCancel: () => void;
  createUploadUrl: CreateUploadUrl;
}

const api = new PatientApi();
const coverageApi = new CoverageApi();

type FormData = Omit<
  AdmissionUpdateRequest,
  'medicalAidAuthorization' | 'attachments' | 'statusHistory' | 'location'
> &
  OxygenFields & {
    medicalAidAuthorizationCode: string;
    medicalAidAuthorizationDate: string;
    media?: FileResult[];
    clinical: AdmissionClinicalData;
    note: AdmissionNotesData;
    authorization: AdmissionAuthorizationData;
    careTeam: PatientGeneralPractitionerUpdateRequest;
    location?: string;
  };

type FormState =
  | 'interactive'
  | 'submitting'
  | 'validation-error'
  | 'submission-error'
  | 'loading'
  | 'complete';

interface ApiData {
  patient: PatientResponse;
  coverage?: PatientCoverageResponse;
}

export const AdmissionUpdateForm: React.FC<AdmissionUpdateFormProps> = ({
  data,
  handleCancel,
  handleSubmit,
  icd10CodeFetcher,
}) => {
  const formState = useFiniteState<FormState>('interactive');
  const formData = useObjectState<Partial<FormData>>({});

  const session = useUserSession();

  const patientId = usePatientId();
  const id = data.id;
  const swrKey = api.getPatientSWRKey(patientId, PatientEndpoint.Base, PatientEndpoint.Coverage);
  const apiData = useSWR(swrKey, async (): Promise<ApiData> => {
    const patient = await api.getPatient(patientId);

    const coverage = await coverageApi.getCoverage(patientId);
    return {
      patient,
      coverage,
    };
  });

  const onSubmit = useCallback(() => {
    const defined = apiData.data?.coverage
      ? formData.getDefined(['authorization', 'clinical', 'note', 'location'])
      : // if we don't have a coverage then we don't expect this in the resulting data
        formData.getDefined(['clinical', 'note', 'location']);

    if (!defined) {
      formState.set('validation-error');
      return;
    }

    const primaryIcd10Code = mapDiagnosesToDiagnosisTypeCode({
      primaryIcd10Code: compact([defined?.authorization?.primaryIcd10Code]),
    });
    const icd10Code = mapDiagnosesToDiagnosisTypeCode({
      icd10Codes: defined?.authorization?.icd10Codes,
    });
    const diagnosis = compact([primaryIcd10Code, icd10Code, defined.clinical.diagnosis]).flat();

    const reasonOfAdmission = compact(
      diagnosis.map(d => {
        const diagnosisCode = d.type === DiagnosisTypeCode.rfa;
        return diagnosisCode ? d.condition : undefined;
      })
    );

    const oxygen = defined.clinical.oxygen;

    handleSubmit({
      service: defined.note.service,
      primaryIcd10Code: defined?.authorization?.primaryIcd10Code || '',
      icd10Codes: defined?.authorization?.icd10Codes,
      periodStart: defined.note.periodStart,
      oxygen: getOxygenValue(oxygen !== undefined, oxygen),
      medicalAidAuthorization: defined.authorization?.medicalAidAuthorization,
      diagnosis,
      pregnancy: defined.clinical.pregnancy,
      location: defined.location,
      reasonOfAdmission,
      id,
    });

    return {};
  }, [apiData.data?.coverage, formData, formState, handleSubmit, id]);

  const { icd10Codes, rfaIcd10Codes } = useMemo(() => {
    const icd10Codes = data?.icd10Codes?.map(code => code.code);
    const acute = data?.acute?.map(code => code.code);
    const chronic = data?.chronic?.map(code => code.code);
    const reasonOfAdmission = data?.reasonOfAdmission?.map(code => code.code);
    const rfaIcd10Codes = { acute, chronic, reasonOfAdmission };

    return { icd10Codes, rfaIcd10Codes, acute, chronic, reasonOfAdmission };
  }, [data]);

  const showErrors = formState.is('validation-error');

  const showLocation = session.hasAny([GroupSecurity.LocationRead]);

  const { type: hasMedicalAid } = AdmissionRules.MedicalAidRules.validateMedicalAidCoverage({
    coverage: apiData.data?.coverage,
  });

  return (
    <SkeletonProvider loading={!data}>
      <Section hasTopMargin>
        <Skeleton>
          <AdmissionStatusDisplay title={strings.CardTitleAdmissionDetails} admission={data} />
        </Skeleton>
      </Section>
      <Section unsetZIndex>
        <Skeleton>
          <AdmissionNotesForm
            onValidChange={formData.set('note')}
            showErrors={showErrors}
            initialValues={data}
            patientId={patientId}
          />
        </Skeleton>
      </Section>

      <Section unsetZIndex isVisible={hasMedicalAid === 'success'}>
        <Skeleton>
          <AdmissionAuthorizationForm
            schemeName={apiData.data?.coverage?.coverage?.scheme?.display}
            planName={apiData.data?.coverage?.coverage?.plan}
            onValidChange={formData.set('authorization')}
            showErrors={showErrors}
            initialValues={data}
            icd10Codes={icd10Codes}
          />
        </Skeleton>
      </Section>

      <Section unsetZIndex>
        <Skeleton>
          <AdmissionClinicalForm
            icd10CodeFetcher={icd10CodeFetcher}
            rfaIcd10Codes={rfaIcd10Codes}
            onValidChange={formData.set('clinical')}
            showErrors={showErrors}
            initialValues={data}
          />
        </Skeleton>
      </Section>

      <Section unsetZIndex isVisible={showLocation}>
        <Skeleton>
          <AdmissionLocationForm
            locationFetcher={locationWardFetcher}
            onValidChange={data => {
              if (data) {
                formData.set('location')(data.location);
              }
            }}
            showErrors={showErrors}
            initialLocation={data?.location}
          />
        </Skeleton>
      </Section>
      <Section unsetZIndex hasBottomMargin>
        <Skeleton>
          <Row justifyContent="flex-end" unsetZIndex>
            <Button onPress={handleCancel} text={strings.ButtonTextCancel} />
            <Spacer width={MARGIN.m} />
            <Button onPress={onSubmit} text={strings.ButtonTextSubmit} />
          </Row>
        </Skeleton>
      </Section>
    </SkeletonProvider>
  );
};
